//#region header */
/**************************************************************************************************
//
//  Description: Hook to fetch application names.
//
//  Copyright:    © 2026 Idox Software Limited
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//#region Version 1.0.0.0 changes
//    001   05.02.26 Sean Flook         GMSCM-12 Initial Revision.
//#endregion Version 1.0.0.0 changes
//
//--------------------------------------------------------------------------------------------------
//#endregion header */

import { useQuery } from "@tanstack/react-query";
import SecurityAPIClient from "../services/securityApiClient";
import ms from "ms";
import useUserQueryStore from "../context/UserStore";
import { ApplicationNameDto } from "../dtos/applicationNamesDto";
import { mergeWithStoredApplicationDetails } from "../utils/ApplicationLookupStorage";

type AnyRecord = Record<string, unknown>;

const asRecord = (value: unknown): AnyRecord | undefined =>
  value !== null && typeof value === "object" && !Array.isArray(value) ? (value as AnyRecord) : undefined;

const pickString = (source: AnyRecord | undefined, ...keys: string[]): string | undefined => {
  if (!source) return undefined;
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string") return value;
  }
  return undefined;
};

const pickStringArray = (source: AnyRecord | undefined, ...keys: string[]): string[] => {
  if (!source) return [];
  for (const key of keys) {
    const value = source[key];
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === "string");
    }
  }
  return [];
};

const normalizeApplicationName = (application: ApplicationNameDto | string | unknown): ApplicationNameDto | undefined => {
  if (typeof application === "string") {
    const name = application.trim();
    if (!name) return undefined;
    return {
      name,
      dbConnectionNames: [],
      elasticConnectionNames: [],
    };
  }

  const record = asRecord(application);
  if (!record) return undefined;

  const name = pickString(record, "name", "Name")?.trim();
  if (!name) return undefined;

  return {
    name,
    dbConnectionNames: pickStringArray(record, "dbConnectionNames", "DbConnectionNames"),
    elasticConnectionNames: pickStringArray(record, "elasticConnectionNames", "ElasticConnectionNames"),
  };
};

const useApplicationNames = () => {
  const currentToken = useUserQueryStore((s) => s.userQuery.currentToken);
  const securityAPIClient = new SecurityAPIClient<ApplicationNameDto | string>("/ApplicationName", currentToken);

  return useQuery({
    queryKey: ["applicationNames"],
    queryFn: securityAPIClient.getAll,
    select: (data: (ApplicationNameDto | string)[]): ApplicationNameDto[] => {
      const mappedData = (Array.isArray(data) ? data : [])
        .map(normalizeApplicationName)
        .filter((application): application is ApplicationNameDto => !!application);

      return mergeWithStoredApplicationDetails(mappedData);
    },
    staleTime: ms("24h"),
  });
};

export default useApplicationNames;
